import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
	Package,
	Plus,
	Edit2,
	Trash2,
	Building2,
	CheckCircle,
	ChevronRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/utils/cn";
import { getAccessToken } from "@/services/auth";
import { useToast } from "@/components/ui/use-toast";
import { adminApiUrl } from "@/config";

// Fetch functions
async function fetchPlans(includeInactive: boolean) {
	const token = getAccessToken();
	const res = await fetch(
		adminApiUrl(`/plans?includeInactive=${includeInactive}`),
		{
			headers: { Authorization: `Bearer ${token}` },
		},
	);
	const data = await res.json();
	return data.data;
}

async function deletePlan(id: string) {
	const token = getAccessToken();
	const res = await fetch(adminApiUrl(`/plans/${id}`), {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}` },
	});
	const data = await res.json();
	if (!data.success) throw new Error(data.message);
	return data;
}

const planColors: Record<string, string> = {
	free: "bg-gray-500",
	starter: "bg-blue-500",
	professional: "bg-purple-500",
	enterprise: "bg-amber-500",
	pro: "bg-purple-500",
	plus: "bg-amber-500",
};

export function PlanList() {
	const [showInactive, setShowInactive] = useState(false);
	const { toast } = useToast();
	const queryClient = useQueryClient();

	const { data: plans, isLoading } = useQuery({
		queryKey: ["admin-plans", showInactive],
		queryFn: () => fetchPlans(showInactive),
	});

	const deleteMutation = useMutation({
		mutationFn: deletePlan,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["admin-plans"] });
			toast({
				title: "Plan deleted",
				description: "The plan has been deactivated successfully.",
			});
		},
		onError: (error: Error) => {
			toast({
				title: "Error",
				description: error.message,
				variant: "destructive",
			});
		},
	});

	const handleDelete = (id: string, name: string) => {
		if (
			window.confirm(
				`Are you sure you want to deactivate the "${name}" plan?`,
			)
		) {
			deleteMutation.mutate(id);
		}
	};

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h1 className="text-2xl font-bold text-gray-900">Plans</h1>
					<p className="text-gray-500 mt-1">
						Manage subscription plans and features
					</p>
				</div>
				<div className="flex items-center gap-4">
					<label className="flex items-center gap-2 text-sm">
						<input
							type="checkbox"
							checked={showInactive}
							onChange={(e) => setShowInactive(e.target.checked)}
							className="rounded border-gray-300"
						/>
						Show inactive
					</label>
					<Link to="/packages">
						<Button variant="outline">
							<Package className="w-4 h-4 mr-2" />
							Custom Packages
						</Button>
					</Link>
					<Link to="/plans/new">
						<Button>
							<Plus className="w-4 h-4 mr-2" />
							Add Plan
						</Button>
					</Link>
				</div>
			</div>

			{/* Plans Grid */}
			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<div className="animate-spin rounded-full h-8 w-8 border-4 border-gray-300 border-t-blue-600" />
				</div>
			) : plans?.length === 0 ? (
				<Card>
					<CardContent className="py-12 text-center text-gray-500">
						No plans found
					</CardContent>
				</Card>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
					{plans?.map((plan: any) => (
						<Card
							key={plan._id}
							className={cn(
								"relative overflow-hidden",
								!plan.isActive && "opacity-60",
							)}
						>
							{/* Plan Color Bar */}
							<div
								className={cn(
									"h-2",
									planColors[plan.name?.toLowerCase()] || "bg-gray-500",
								)}
							/>

							<CardHeader className="pb-2">
								<CardTitle className="flex items-center justify-between">
									<div className="flex items-center gap-2">
										<span>{plan.displayName || plan.name}</span>
										{!plan.isActive && (
											<Badge className="bg-gray-100 text-gray-600">
												Inactive
											</Badge>
										)}
									</div>
									<div className="flex items-center gap-1">
										<Link to={`/plans/${plan._id}`}>
											<Button variant="ghost" size="sm">
												<Edit2 className="w-4 h-4" />
											</Button>
										</Link>
										<Button
											variant="ghost"
											size="sm"
											onClick={() => handleDelete(plan._id, plan.name)}
											disabled={deleteMutation.isPending}
										>
											<Trash2 className="w-4 h-4 text-red-500" />
										</Button>
									</div>
								</CardTitle>
							</CardHeader>

							<CardContent className="space-y-4">
								{plan.description && (
									<p className="text-sm text-gray-500">{plan.description}</p>
								)}

								{/* Pricing */}
								{plan.price && (
									<div className="flex items-baseline gap-1">
										<span className="text-2xl font-bold">
											${plan.price.monthly || 0}
										</span>
										<span className="text-gray-500">/month</span>
									</div>
								)}

								{/* Stats */}
								<div className="flex items-center gap-4 pt-2 border-t">
									<div className="flex items-center gap-2 text-sm">
										<Building2 className="w-4 h-4 text-gray-400" />
										<span>
											{plan.organizationCount || 0} organization
											{plan.organizationCount !== 1 ? "s" : ""}
										</span>
									</div>
									<div className="flex items-center gap-2 text-sm">
										<CheckCircle className="w-4 h-4 text-gray-400" />
										<span>
											{plan.features?.length || 0} feature
											{plan.features?.length !== 1 ? "s" : ""}
										</span>
									</div>
								</div>

								{/* Highlights & Features Preview */}
								{plan.highlights && plan.highlights.length > 0 ? (
									<div className="pt-2 border-t">
										<p className="text-sm font-medium text-gray-700 mb-2">
											Highlights
										</p>
										<div className="space-y-1">
											{plan.highlights.slice(0, 5).map((highlight: string, i: number) => (
												<div
													key={i}
													className="flex items-center gap-2 text-sm"
												>
													<CheckCircle className="w-3 h-3 text-green-500" />
													<span className="text-gray-600">{highlight}</span>
												</div>
											))}
											{plan.highlights.length > 5 && (
												<p className="text-sm text-gray-500 ml-5">
													+{plan.highlights.length - 5} more
												</p>
											)}
										</div>
										{plan.features && plan.features.length > 0 && (
											<p className="text-xs text-gray-400 mt-2">
												{plan.features.length} permission{plan.features.length !== 1 ? "s" : ""} included
											</p>
										)}
									</div>
								) : plan.features && plan.features.length > 0 ? (
									<div className="pt-2 border-t">
										<p className="text-sm font-medium text-gray-700 mb-2">
											Features
										</p>
										<div className="space-y-1">
											{plan.features.slice(0, 5).map((feature: any) => (
												<div
													key={feature._id || feature.code}
													className="flex items-center gap-2 text-sm"
												>
													<CheckCircle className="w-3 h-3 text-green-500" />
													<span className="text-gray-600">
														{feature.name || feature.code}
													</span>
												</div>
											))}
											{plan.features.length > 5 && (
												<p className="text-sm text-gray-500 ml-5">
													+{plan.features.length - 5} more
												</p>
											)}
										</div>
									</div>
								) : null}

								{/* View Details Link */}
								<Link
									to={`/plans/${plan._id}`}
									className="flex items-center justify-end gap-1 text-sm text-blue-600 hover:underline pt-2"
								>
									View details
									<ChevronRight className="w-4 h-4" />
								</Link>
							</CardContent>
						</Card>
					))}
				</div>
			)}
		</div>
	);
}
